import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppProvider } from './context/AppContext';
import { SessionProvider } from './contexts/SessionContext';
import { AuthProvider } from './contexts/AuthContext';
import SessionTimeoutManager from './components/SessionTimeoutManager';
import { CartProvider } from './contexts/CartContext';
import { PaymentProvider } from './contexts/PaymentContext';
import './index.css';
import { KeyboardProvider, KeyboardOverlay } from './context/KeyboardContext';
import PrivateRoute from './components/PrivateRoute';
import LoginPage from './pages/LoginPage';
import OtpPage from './pages/OtpPage';
import DashboardKotilingaPage from './pages/DashboardKotilingaPage';
import DashboardTemplePage from './pages/DashboardTemplePage';
import PledgesPage from './pages/PledgesPage';
import PostPaymentPage from './pages/PostPaymentPage';
import StripeReconciliationNewPage from './pages/recon/StripeReconciliationNewPage';
import StripePayoutsPage from './pages/recon/StripePayoutsPage';
import AuthnetReconciliationNewPage from './pages/recon/AuthnetReconciliationNewPage';
import AuthnetDashboardNewPage from './pages/recon/AuthnetDashboardNewPage';
import AuthnetSubscriptionsNewPage from './pages/recon/AuthnetSubscriptionsNewPage';
import ZelleReconciliationPage from './pages/ZelleReconciliationPage';
import PaymentAlertsPage from './pages/PaymentAlertsPage';
import RecurringReportPage from './pages/RecurringReportPage';
import PaysimpleReconciliationNewPage from './pages/recon/PaysimpleReconciliationNewPage';
import PaysimpleDashboardNewPage from './pages/recon/PaysimpleDashboardNewPage';
import PaysimpleSubscriptionsNewPage from './pages/recon/PaysimpleSubscriptionsNewPage';
import SolarFleetPage from './pages/solar/SolarFleetPage';
import SolarSitePage from './pages/solar/SolarSitePage';
import SpecialSponsorshipsPage from './pages/SpecialSponsorshipsPage';
import SpecialSponsorshipProgramPage from './pages/SpecialSponsorshipProgramPage';
import DevoteeSearchPage from './pages/DevoteeSearchPage';
import DevoteeDetailPage from './pages/DevoteeDetailPage';
import OrdersSearchPage from './pages/OrdersSearchPage';
import OrderEditPage from './pages/OrderEditPage';
import OrderUpdatedPage from './pages/OrderUpdatedPage';
import OrderDetailPage from './pages/OrderDetailPage';
import UsersPage from './pages/UsersPage';
import RolesPage from './pages/RolesPage';
import ManagePage from './pages/ManagePage';
import VenkateswaraSahasranamaMalaReportPage from './pages/VenkateswaraSahasranamaMalaReportPage';
import KotilingaInventoryPage from './pages/KotilingaInventoryPage';
import KotilingaInventoryByCategoryPage from './pages/KotilingaInventoryByCategoryPage';
import KotilingaOrderInventoryPage from './pages/KotilingaOrderInventoryPage';
import KotilingaSponsorsByProductPage from './pages/KotilingaSponsorsByProductPage';
import KotilingaTreeRevenueReportPage from './pages/KotilingaTreeRevenueReportPage';
import SriChakramLocatorPage from './pages/SriChakramLocatorPage';
import TempleTreeRevenueReportPage from './pages/TempleTreeRevenueReportPage';
import ManageEmailTemplatesPage from './pages/ManageEmailTemplatesPage';
import ManageCalendarEventsPage from './pages/ManageCalendarEventsPage';
import ScheduledEmailsPage from './pages/ScheduledEmailsPage';
import ManageAlertsRemindersPage from './pages/ManageAlertsRemindersPage';
import ManageRostersPage from './pages/ManageRostersPage';
import LifeMembershipEligibilityPage from './pages/LifeMembershipEligibilityPage';
import ManageKioskPrimeProductsPage from './pages/ManageKioskPrimeProductsPage';
import ManageSponsorBenefitsPage from './pages/ManageSponsorBenefitsPage';
import ManageCiviWcProductMappingPage from './pages/ManageCiviWcProductMappingPage';
import ManageCiviContributionsMigratedPage from './pages/ManageCiviContributionsMigratedPage';
import ManageCiviMigrationLogsPage from './pages/ManageCiviMigrationLogsPage';
import ManageCiviMigrationJobsPage from './pages/ManageCiviMigrationJobsPage';
import MeetingsPage from './pages/MeetingsPage';
import WelcomePage from './pages/WelcomePage';
import EventReportsPage from './pages/EventReportsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import KotilingaTrendsPage from './pages/KotilingaTrendsPage';
import ScopedDashboardPage from './pages/TempleSponsorshipsPage';
import TempleTrendsPage from './pages/TempleTrendsPage';
import TempleCampaignsPage from './pages/TempleCampaignsPage';
import ReportsPage from './pages/ReportsPage';
import SponsorsReportPage from './pages/SponsorsReportPage';
import SettingsSyncPage from './pages/SettingsSyncPage';
import SystemLogPage from './pages/SystemLogPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a237e',
      light: '#534bae',
      dark: '#000051',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff6d00',
      light: '#ff9e40',
      dark: '#c43c00',
      contrastText: '#000000',
    },
    background: {
      default: '#f5f5f7',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#4a4a4a',
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 600,
      color: '#1a237e',
    },
    h2: {
      fontWeight: 500,
      color: '#1a237e',
    },
    h3: {
      fontWeight: 500,
      color: '#1a237e',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary'
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          fontWeight: 600,
          letterSpacing: 0.2,
          transition: 'all 0.15s ease-in-out',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiCircularProgress: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            transition: 'border-color 0.15s ease-in-out',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1a237e',
            borderWidth: 2,
          },
        },
        input: {
          backgroundColor: '#ffffff',
        },
      },
    },
  },
});

// Wrapper component to provide navigation to SessionProvider
const SessionProviderWrapper = ({ children }) => {
  const navigate = useNavigate();
  return (
    <SessionProvider navigate={navigate}>
      {children}
    </SessionProvider>
  );
};

// Get the root element
const rootElement = document.getElementById('root');

if (!rootElement) {
  console.error('Failed to find the root element');
} else {
  console.log('Root element found, creating React root');
  try {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <KeyboardProvider>
            <AppProvider>
              <Router>
                <SessionProviderWrapper>
                  <AuthProvider>
                  <SessionTimeoutManager />
                  <CartProvider>
                    <PaymentProvider>
                      <Routes>
                        {/* Public auth routes */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/otp" element={<OtpPage />} />

                        {/* Public / restricted Sri Chakram locator (no staff login) */}
                        <Route path="/srichakra" element={<SriChakramLocatorPage mode="public" />} />
                        <Route path="/srichakra/private" element={<SriChakramLocatorPage mode="restricted" />} />

                        {/* Back-office private routes */}
                        <Route element={<PrivateRoute />}> 
                          <Route path="/welcome" element={<WelcomePage />} />
                          <Route path="/dashboard" element={<Navigate to="/dashboard/temple" replace />} />
                          <Route path="/dashboard/kotilinga" element={<DashboardKotilingaPage />} />
                          <Route path="/dashboard/temple" element={<DashboardTemplePage />} />
                          <Route path="/pledges" element={<PledgesPage />} />
                          <Route path="/post-payment" element={<PostPaymentPage />} />
                          <Route path="/payments/stripe/reconciliation" element={<StripeReconciliationNewPage />} />
                          <Route path="/payments/stripe/payouts" element={<StripePayoutsPage />} />
                          <Route path="/payments/stripe-reconciliation" element={<Navigate to="/payments/stripe/reconciliation" replace />} />
                          <Route path="/payments/authnet" element={<AuthnetDashboardNewPage />} />
                          <Route path="/payments/authnet/subscriptions" element={<AuthnetSubscriptionsNewPage />} />
                          <Route path="/payments/authnet/reconciliation" element={<AuthnetReconciliationNewPage />} />
                          <Route path="/payments/authnet-reconciliation" element={<Navigate to="/payments/authnet/reconciliation" replace />} />
                          <Route path="/payments/zelle-reconciliation" element={<ZelleReconciliationPage />} />
                          <Route path="/payments/alerts" element={<PaymentAlertsPage />} />
                          <Route path="/payments/recurring" element={<RecurringReportPage />} />
                          <Route path="/payments/paysimple" element={<PaysimpleDashboardNewPage />} />
                          <Route path="/payments/paysimple/subscriptions" element={<PaysimpleSubscriptionsNewPage />} />
                          <Route path="/payments/paysimple/reconciliation" element={<PaysimpleReconciliationNewPage />} />
                          <Route path="/payments/paysimple/schedules" element={<Navigate to="/payments/paysimple/subscriptions" replace />} />
                          <Route path="/manage/solar" element={<SolarFleetPage />} />
                          <Route path="/manage/solar/:id" element={<SolarSitePage />} />
                          <Route path="/solar" element={<Navigate to="/manage/solar" replace />} />
                          <Route path="/solar/:id" element={<SolarSitePage />} />
                          <Route path="/devotees" element={<DevoteeSearchPage />} />
                          <Route path="/devotees/:id" element={<DevoteeDetailPage />} />
                          <Route path="/orders" element={<OrdersSearchPage />} />
                          <Route path="/orders/:orderId/edit" element={<OrderEditPage />} />
                          <Route path="/orders/:orderId/updated" element={<OrderUpdatedPage />} />
                          <Route path="/orders/:id" element={<OrderDetailPage />} />
                          <Route path="/manage" element={<ManagePage />} />
                          <Route path="/manage/venkateswara-sahasranama-mala" element={<Navigate to="/temple/venkateswara-sahasranama-mala" replace />} />
                          <Route path="/manage/kotilinga-inventory" element={<Navigate to="/kotilinga/inventory" replace />} />
                          <Route path="/manage/email-templates" element={<ManageEmailTemplatesPage />} />
                          <Route path="/manage/calendar-events" element={<ManageCalendarEventsPage />} />
                          <Route path="/manage/scheduled-emails" element={<ScheduledEmailsPage />} />
                          <Route path="/manage/alerts-reminders" element={<ManageAlertsRemindersPage />} />
                          <Route path="/manage/rosters" element={<ManageRostersPage />} />
                          <Route path="/manage/life-membership-eligibility" element={<LifeMembershipEligibilityPage />} />
                          <Route path="/manage/special-sponsorships" element={<SpecialSponsorshipsPage />} />
                          <Route path="/manage/special-sponsorships/:slug" element={<SpecialSponsorshipProgramPage />} />
                          <Route path="/manage/kiosk-prime-products" element={<ManageKioskPrimeProductsPage />} />
                          <Route path="/manage/sponsor-benefits" element={<ManageSponsorBenefitsPage />} />
                          <Route path="/manage/civi-wc-product-mapping" element={<ManageCiviWcProductMappingPage />} />
                          <Route path="/manage/civi-contributions-migrated" element={<ManageCiviContributionsMigratedPage />} />
                          <Route path="/manage/civi-migration-jobs" element={<ManageCiviMigrationJobsPage />} />
                          <Route path="/manage/civi-migration-logs" element={<ManageCiviMigrationLogsPage />} />
                          <Route path="/manage/users" element={<UsersPage />} />
                          <Route path="/manage/roles" element={<RolesPage />} />
                          <Route path="/reports" element={<ReportsPage />} />
                          <Route path="/reports/event-reports" element={<EventReportsPage />} />
                          <Route path="/reports/sponsors" element={<SponsorsReportPage />} />
                          <Route path="/analytics" element={<AnalyticsPage />} />
                          {/* Kotilinga stack */}
                          <Route path="/kotilinga/sponsorships" element={<ScopedDashboardPage />} />
                          <Route path="/kotilinga/pledges" element={<Navigate to="/pledges?scope=kotilinga" replace />} />
                          <Route path="/kotilinga/inventory" element={<KotilingaInventoryPage />} />
                          <Route path="/kotilinga/inventory-by-category" element={<KotilingaInventoryByCategoryPage />} />
                          <Route path="/kotilinga/inventory-new" element={<Navigate to="/kotilinga/inventory-orders" replace />} />
                          <Route path="/kotilinga/inventory-orders" element={<KotilingaOrderInventoryPage />} />
                          <Route path="/kotilinga/sponsors-by-product" element={<KotilingaSponsorsByProductPage />} />
                          <Route path="/kotilinga/tree-revenue-report" element={<KotilingaTreeRevenueReportPage />} />
                          <Route path="/kotilinga/sri-chakram-locator" element={<SriChakramLocatorPage mode="admin" />} />
                          <Route path="/kotilinga/analytics" element={<Navigate to="/analytics?view=kotilinga" replace />} />
                          <Route path="/kotilinga/trends" element={<KotilingaTrendsPage />} />
                          {/* Temple stack */}
                          <Route path="/temple/sponsorships" element={<ScopedDashboardPage />} />
                          <Route path="/temple/tree-revenue-report" element={<TempleTreeRevenueReportPage />} />
                          <Route path="/temple/pledges" element={<Navigate to="/pledges?scope=temple" replace />} />
                          <Route path="/temple/inventory" element={<Navigate to="/temple/sponsorships" replace />} />
                          <Route path="/temple/analytics" element={<Navigate to="/analytics?view=temple" replace />} />
                          <Route path="/temple/trends" element={<TempleTrendsPage />} />
                          <Route path="/temple/venkateswara-sahasranama-mala" element={<VenkateswaraSahasranamaMalaReportPage />} />
                          <Route path="/temple/campaigns" element={<TempleCampaignsPage />} />
                          <Route path="/meetings" element={<MeetingsPage />} />
                          {/* Settings chrome retired — these render as standalone pages, reached from Manage. */}
                          <Route path="/settings" element={<Navigate to="/settings/sync" replace />} />
                          <Route path="/settings/sync" element={<SettingsSyncPage />} />
                          <Route path="/settings/system-log" element={<SystemLogPage />} />
                        </Route>
                      <Route path="/" element={<Navigate to="/login" replace />} />
                      </Routes>
                    </PaymentProvider>
                  </CartProvider>
                  </AuthProvider>
                </SessionProviderWrapper>
              </Router>
              {/* Global keyboard overlay */}
              <React.Suspense fallback={null}>
                <KeyboardOverlay />
              </React.Suspense>
            </AppProvider>
          </KeyboardProvider>
        </ThemeProvider>
      </React.StrictMode>
    );
    console.log('React root rendered successfully');
  } catch (error) {
    console.error('Error rendering React app:', error);
  }
}
